import axiosInstance from '@/app/api/axiosInstance';
import {
  Vehicle,
  VehicleCreate,
  VehicleUpdate,
  VehicleListItem,
} from '@/app/types/vehicle';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Helper to parse vehicle response
const parseVehicleResponse = (vehicle: Vehicle): Vehicle => ({
  ...vehicle,
  // Ensure all fields are properly typed
  year_of_manufacture: vehicle.year_of_manufacture ? Number(vehicle.year_of_manufacture) : null,
  last_service_odometer: vehicle.last_service_odometer ? Number(vehicle.last_service_odometer) : null,
});

// Helper to parse list item
const parseVehicleListItem = (item: VehicleListItem): VehicleListItem => ({
  ...item,
  year_of_manufacture: item.year_of_manufacture ? Number(item.year_of_manufacture) : null,
  last_service_odometer: item.last_service_odometer ? Number(item.last_service_odometer) : null,
});

/**
 * List vehicles with pagination and filtering
 */
export const listVehicles = async (
  params: ListQueryParams & { owner_id?: number }
): Promise<PaginatedResponse<VehicleListItem>> => {
  const response = await axiosInstance.get<PaginatedResponse<VehicleListItem>>('/vehicles/', {
    params,
  });
  response.data.items = response.data.items.map(parseVehicleListItem);
  return response.data;
};

/**
 * Get a single vehicle by ID
 */
export const getVehicle = async (id: number): Promise<Vehicle> => {
  const response = await axiosInstance.get<Vehicle>(`/vehicles/${id}`);
  return parseVehicleResponse(response.data);
};

/**
 * Get a vehicle by registration number
 */
export const getVehicleByRegistration = async (registrationNumber: string): Promise<Vehicle> => {
  const response = await axiosInstance.get<Vehicle>(
    `/vehicles/by-registration/${encodeURIComponent(registrationNumber)}`
  );
  return parseVehicleResponse(response.data);
};

/**
 * Create a new vehicle
 */
export const createVehicle = async (data: VehicleCreate): Promise<Vehicle> => {
  const payload = {
    ...data,
    // Ensure numeric fields are sent correctly
    year_of_manufacture: data.year_of_manufacture ? Number(data.year_of_manufacture) : null,
  };

  const response = await axiosInstance.post<Vehicle>('/vehicles/', payload);
  return parseVehicleResponse(response.data);
};

/**
 * Update an existing vehicle
 */
export const updateVehicle = async (id: number, data: VehicleUpdate): Promise<Vehicle> => {
  const payload = {
    ...data,
    // Ensure numeric fields are sent correctly
    ...(data.year_of_manufacture !== undefined && {
      year_of_manufacture: data.year_of_manufacture ? Number(data.year_of_manufacture) : null,
    }),
  };

  const response = await axiosInstance.patch<Vehicle>(`/vehicles/${id}`, payload);
  return parseVehicleResponse(response.data);
};

/**
 * Delete a vehicle
 * Note: Will fail if vehicle has existing job cards
 */
export const deleteVehicle = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/vehicles/${id}`);
};

/**
 * Search vehicles (for vehicle selector component)
 */
export const searchVehicles = async (
  query: string,
  ownerId?: number,
  limit: number = 20
): Promise<VehicleListItem[]> => {
  const params: ListQueryParams & { owner_id?: number } = {
    search_query: query,
    limit,
  };

  if (ownerId) {
    params.owner_id = ownerId;
  }

  const response = await listVehicles(params);
  return response.items;
};
